<template>
    <Teleport to="body">
        <!-- Modal Backdrop -->
        <transition name="fade">
            <div v-if="show" class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" @click="close">
                <!-- Modal Content -->
                <div class="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl" @click.stop>
                    <!-- Header -->
                    <div class="sticky top-0 bg-white px-6 py-4 border-b border-gray-100 flex items-center justify-between z-10">
                        <div>
                            <h2 class="text-lg font-bold text-gray-900">{{ title }}</h2>
                            <p class="text-xs text-gray-500 mt-1">{{ subtitle }}</p>
                        </div>
                        <button aria-label="Close" @click="close" class="text-gray-500 hover:text-gray-700 p-2 hover:bg-gray-100 rounded-lg transition-colors">
                            <i class="fas fa-times text-lg"></i>
                        </button>
                    </div>

                    <!-- Tabs -->
                    <div class="border-b border-gray-200 bg-gray-50">
                        <div class="flex px-6">
                            <button aria-label="Action" @click="setActiveTab('followers')"
                                :class="[
                                    'px-4 py-3 font-bold text-sm border-b-2 transition-all duration-300',
                                    activeTab === 'followers' 
                                        ? 'border-blue-600 text-blue-600 bg-white rounded-t-lg' 
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-white/50'
                                ]"
                            >
                                <i class="fas fa-users mr-2 text-xs"></i>
                                Followers ({{ statistics.followers_count || 0 }})
                            </button>
                            <button aria-label="Action" @click="setActiveTab('following')"
                                :class="[
                                    'px-4 py-3 font-bold text-sm border-b-2 transition-all duration-300',
                                    activeTab === 'following' 
                                        ? 'border-blue-600 text-blue-600 bg-white rounded-t-lg' 
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-white/50'
                                ]"
                            >
                                <i class="fas fa-user-plus mr-2 text-xs"></i>
                                Following ({{ statistics.following_count || 0 }})
                            </button>
                        </div>
                    </div>

                    <!-- Content -->
                    <div class="p-4 overflow-y-auto" style="max-height: calc(90vh - 140px)">
                        <!-- Loading State -->
                        <div v-if="loading" class="text-center py-8">
                            <div class="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                            <p class="text-gray-500 text-sm">Loading {{ activeTab }}...</p>
                        </div>

                        <!-- Connections List -->
                        <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            <div 
                                v-for="user in connections" 
                                :key="user.id"
                                class="bg-white rounded-xl p-4 text-center transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:ring-2 hover:ring-blue-400 border-2 border-gray-100"
                            >
                                <!-- Profile Image -->
                                <div class="relative w-14 h-14 mx-auto mb-3 cursor-pointer" @click.stop="gotoProfile(user)">
                                    <!-- Image or Initial -->
                                    <div v-if="user.profile_picture" 
                                        class="w-full h-full rounded-full border-2 border-white shadow-md overflow-hidden transition-opacity duration-300 hover:opacity-90">
                                        <img 
                                            :src="IMAGE_BASE + user.profile_picture"
                                            class="w-full h-full object-cover"
                                            :alt="user.name || 'Profile picture'"
                                            loading="lazy">
                                    </div>
                                    <!-- First Letter Fallback -->
                                    <div v-else 
                                        class="w-full h-full rounded-full border-2 border-white shadow-md flex items-center justify-center text-white font-bold text-lg bg-gradient-to-r from-blue-500 to-blue-600 transition-opacity duration-300 hover:opacity-90">
                                        {{ getFirstLetter(user.name) }}
                                    </div>
                                </div>
                                
                                <!-- User Info -->
                                <div class="mb-3">
                                    <div class="font-bold text-gray-800 text-sm truncate cursor-pointer mb-1" 
                                        @click.stop="gotoProfile(user)">
                                        {{ user.name }}
                                    </div>
                                    <div class="text-xs text-gray-500 truncate mb-1">@{{ user.user_name }}</div>
                                    <div v-if="user.school_name" class="text-xs text-gray-600 truncate">{{ user.school_name }}</div>
                                </div>
                                
                                <!-- Location -->
                                <div v-if="user.city || user.state" class="flex justify-center items-center gap-1 mb-3 text-xs text-gray-500">
                                    <i class="fas fa-map-marker-alt text-blue-500"></i>
                                    <span class="truncate">{{ user.city || '' }}{{ user.city && user.state ? ', ' : '' }}{{ user.state || '' }}</span>
                                </div>
                                
                                <!-- Follow Button States -->
                                <div v-if="!user.is_current_user">
                                    <!-- Request Pending State -->
                                    <button v-if="user.has_pending_request" 
                                            :disabled="true"
                                            class="w-full py-2 rounded-lg font-bold text-xs bg-gradient-to-r from-gray-50 to-gray-100 text-gray-600 border-2 border-gray-300 cursor-not-allowed flex items-center justify-center gap-2 transition-all duration-300">
                                        <i class="fas fa-hourglass-half text-xs"></i>
                                        Request Pending
                                    </button>
                                    
                                    <!-- Following State -->
                                    <button aria-label="Action" v-else-if="user.is_following" 
                                            @click="toggleUserFollow(user.id)"
                                            :class="[
                                                'w-full py-2 rounded-lg font-bold text-xs transition-all duration-300 flex items-center justify-center gap-2',
                                                user.followLoading ? 'opacity-60 cursor-wait' : 'hover:bg-gray-200 hover:border-gray-300',
                                                'bg-gradient-to-r from-gray-50 to-gray-100 text-gray-700 border-2 border-gray-200'
                                            ]" 
                                            :disabled="user.followLoading">
                                        <i v-if="user.followLoading" class="fas fa-spinner fa-spin"></i>
                                        <i v-else class="fas fa-user-check"></i>
                                        {{ user.followLoading ? 'Loading...' : 'Following' }}
                                    </button>
                                    
                                    <!-- Follow State (Default) -->
                                    <button aria-label="Action" v-else 
                                            @click="toggleUserFollow(user.id)"
                                            :class="[
                                                'w-full py-2 rounded-lg font-bold text-xs transition-all duration-300 flex items-center justify-center gap-2',
                                                user.followLoading ? 'opacity-60 cursor-wait' : 'hover:opacity-90',
                                                'bg-gradient-to-r from-blue-500 to-blue-600 text-white border-2 border-blue-500'
                                            ]" 
                                            :disabled="user.followLoading">
                                        <i v-if="user.followLoading" class="fas fa-spinner fa-spin"></i>
                                        <i v-else class="fas fa-user-plus"></i>
                                        {{ user.followLoading ? 'Loading...' : 'Follow' }}
                                    </button>
                                </div>
                                
                                <!-- Current User Indicator -->
                                <div v-else class="w-full py-2 text-xs text-gray-500 font-bold text-center bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg border-2 border-gray-200 transition-all duration-300">
                                    <i class="fas fa-user mr-1"></i>
                                    You
                                </div>
                            </div>
                        </div>
                        
                        <!-- Empty State -->
                        <div v-if="!loading && connections.length === 0" class="text-center py-8">
                            <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                <i class="fas fa-users text-gray-400 text-xl"></i>
                            </div>
                            <p class="text-gray-500 text-sm">
                                {{ activeTab === 'followers' ? 'No followers yet' : 'Not following anyone yet' }}
                            </p>
                            <p class="text-xs text-gray-400 mt-1">
                                {{ activeTab === 'followers' ? 'Start sharing to get followers' : 'Discover and follow other students' }}
                            </p>
                        </div>

                        <!-- Load More -->
                        <div v-if="connections.length > 0 && hasMoreConnections" class="text-center mt-4">
                            <button aria-label="Action" @click="loadMore" 
                                :disabled="loading"
                                class="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-bold text-sm hover:bg-gray-200 transition-all duration-300 border-2 border-gray-200 disabled:opacity-50 flex items-center gap-2 mx-auto"
                            >
                                <i v-if="loading" class="fas fa-spinner fa-spin"></i>
                                <span>Load More</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </Teleport>
</template>

<script>
import { ref, watch, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { getUserFollows, followUser, unfollowUser, IMAGE_BASE } from '@/services/api'

export default {
    name: 'ConnectionsPopup',
    props: {
        show: {
            type: Boolean,
            default: false
        },
        userId: {
            type: [String, Number],
            required: true
        },
        statistics: {
            type: Object,
            default: () => ({
                followers_count: 0,
                following_count: 0
            })
        },
        initialTab: {
            type: String,
            default: 'followers',
            validator: (value) => ['followers', 'following'].includes(value)
        }
    },
    emits: ['update:show', 'followStatusChanged'],
    setup(props, { emit }) {
        const activeTab = ref(props.initialTab)
        const router = useRouter()
        const loading = ref(false)
        const connections = ref([])
        const hasMoreConnections = ref(false)

        // Cached data for both tabs
        const cachedData = ref({
            followers: [],
            following: []
        })

        const title = computed(() => {
            return activeTab.value === 'followers' ? 'Followers' : 'Following'
        })

        const subtitle = computed(() => {
            return activeTab.value === 'followers' 
                ? `People following this user` 
                : `People this user is following`
        })

        const close = () => {
            console.log('[ConnectionsPopup] Closing modal')
            emit('update:show', false)
        }

        const setActiveTab = async (tab) => {
            if (activeTab.value === tab) return
            
            activeTab.value = tab
            console.log(`[ConnectionsPopup] Switching to tab: ${tab}`)
            await loadConnections()
        }

        const loadConnections = async () => {
            console.log(`[ConnectionsPopup] Loading connections for tab: ${activeTab.value}`)
            
            // Use cached data if available
            if (cachedData.value[activeTab.value].length > 0) {
                console.log(`[ConnectionsPopup] Using cached data for ${activeTab.value}`)
                connections.value = cachedData.value[activeTab.value].map(user => ({
                    ...user,
                    followLoading: false,
                    is_following: user.is_following || false,
                    has_pending_request: user.has_pending_request || false,
                    is_current_user: user.id === getCurrentUserId()
                }))
                return
            }

            loading.value = true
            try {
                console.log(`[ConnectionsPopup] Making API call for ${activeTab.value}`)
                const response = await getUserFollows(props.userId, {
                    type: activeTab.value,
                    page: 1,
                    per_page: 12
                })

                console.log(`[ConnectionsPopup] API response for ${activeTab.value}:`, response)

                let connectionsData = []
                if (response.data?.users) {
                    connectionsData = response.data.users
                } else if (response.users) {
                    connectionsData = response.users
                } else if (Array.isArray(response.data)) {
                    connectionsData = response.data
                } else if (Array.isArray(response)) {
                    connectionsData = response
                }

                console.log(`[ConnectionsPopup] Parsed connections data:`, connectionsData)

                // Cache the data
                cachedData.value[activeTab.value] = connectionsData

                connections.value = connectionsData.map(user => ({
                    ...user,
                    followLoading: false,
                    // Normalize boolean values from API (can be true/false, 1/0, or string)
                    is_following: (user.is_following === true) || (user.is_following === 1) || (user.is_following === '1') || false,
                    has_pending_request: (user.has_pending_request === true) || (user.has_pending_request === 1) || (user.has_pending_request === '1') || false,
                    is_current_user: user.id === getCurrentUserId()
                }))

                console.log(`[ConnectionsPopup] Loaded ${activeTab.value}:`, connections.value);

                hasMoreConnections.value = false // You can implement pagination here

            } catch (err) {
                console.error(`[ConnectionsPopup] Error loading ${activeTab.value}:`, err)
                connections.value = []
            } finally {
                loading.value = false
            }
        }

        const getCurrentUserId = () => {
            // Get current user ID from cookie or store
            const userCookie = document.cookie.split('; ').find(row => row.startsWith('user='))
            if (userCookie) {
                try {
                    const userData = JSON.parse(decodeURIComponent(userCookie.split('=')[1]))
                    return userData.id
                } catch (error) {
                    console.error('Error parsing user cookie:', error)
                }
            }
            return null
        }

        const getFirstLetter = (name) => {
            if (!name) return '?'
            return name.charAt(0).toUpperCase()
        }

        const gotoProfile = async (user) => {
            if (!user) return
            const username = user.user_name || user.username || user.userName || user.id
            console.log(`[ConnectionsPopup] Navigating to profile: ${username}`)
            // Close the modal first, then navigate to /profile/{username}
            try {
                // Emit the update to close the modal so the parent can hide it immediately
                emit('update:show', false)
                await router.push(`/profile/${username}`)
            } catch (err) {
                console.error('Navigation error:', err)
            }
        }

        const toggleUserFollow = async (targetUserId) => {            
            const userIndex = connections.value.findIndex(u => u.id === targetUserId)
            if (userIndex === -1) return

            connections.value[userIndex].followLoading = true
            try {
                const user = connections.value[userIndex]

                if (user.is_following) {
                    // UNFOLLOW
                    console.log(`[ConnectionsPopup] Unfollowing user ${targetUserId}`)
                    await unfollowUser(targetUserId)
                    connections.value[userIndex].is_following = false
                    connections.value[userIndex].has_pending_request = false
                    
                    // Update statistics
                    emit('followStatusChanged', {
                        type: 'unfollow',
                        userId: targetUserId,
                        is_following: false,
                        has_pending_request: false,
                        tab: activeTab.value
                    })
                } else if (user.has_pending_request) {
                    // CANCEL PENDING REQUEST
                    console.log(`[ConnectionsPopup] Cancelling pending request for user ${targetUserId}`)
                    // Assuming you have a cancelFollowRequest API function
                    // await cancelFollowRequest(targetUserId);
                    connections.value[userIndex].has_pending_request = false
                    
                    emit('followStatusChanged', {
                        type: 'pending_cancelled',
                        userId: targetUserId,
                        is_following: false,
                        has_pending_request: false,
                        tab: activeTab.value
                    })
                } else {
                    // FOLLOW (may create pending request or follow immediately)
                    console.log(`[ConnectionsPopup] Following user ${targetUserId}`)
                    const response = await followUser(targetUserId)
                    const data = response?.data?.data || response?.data || response || {}
                    
                    console.log(`[ConnectionsPopup] Follow response:`, data)
                    
                    // Update based on API response
                    connections.value[userIndex].is_following = Boolean(data.is_following)
                    connections.value[userIndex].has_pending_request = Boolean(data.is_pending || data.has_pending_request)
                    
                    emit('followStatusChanged', {
                        type: connections.value[userIndex].is_following ? 'follow' : 'pending',
                        userId: targetUserId,
                        is_following: connections.value[userIndex].is_following,
                        has_pending_request: connections.value[userIndex].has_pending_request,
                        tab: activeTab.value,
                        pending_request_id: data.pending_request_id || data.request_id
                    })
                }

                // Update cache
                const cacheIndex = cachedData.value[activeTab.value].findIndex(u => u.id === targetUserId)
                if (cacheIndex !== -1) {
                    cachedData.value[activeTab.value][cacheIndex].is_following = connections.value[userIndex].is_following
                    cachedData.value[activeTab.value][cacheIndex].has_pending_request = connections.value[userIndex].has_pending_request
                }

            } catch (err) {
                console.error('❌ Error toggling user follow:', err)
            } finally {
                connections.value[userIndex].followLoading = false
            }
        }

        const loadMore = () => {
            console.log('Load more functionality - implement pagination')
        }

        watch(() => props.show, (newVal) => {
            console.log(`[ConnectionsPopup] show prop changed: ${newVal}`)
            if (newVal) {
                console.log(`[ConnectionsPopup] Modal should be visible, loading connections...`)
                // Always reset tab and load connections on open
                activeTab.value = props.initialTab;
                loadConnections();
            } else {
                console.log(`[ConnectionsPopup] Modal should be hidden`)
            }
        })

        watch(() => props.initialTab, (newTab) => {
            console.log(`[ConnectionsPopup] initialTab prop changed to: ${newTab}`)
            activeTab.value = newTab
            if (props.show) {
                loadConnections()
            }
        })

        onMounted(() => {
            console.log('[ConnectionsPopup] Component mounted')
            console.log('[ConnectionsPopup] Initial props:', {
                show: props.show,
                userId: props.userId,
                initialTab: props.initialTab
            })
            // If modal is open on mount, load data immediately
            if (props.show) {
                activeTab.value = props.initialTab;
                loadConnections();
            }
        })

        return {
            activeTab,
            loading,
            connections,
            hasMoreConnections,
            IMAGE_BASE,
            title,
            subtitle,
            close,
            setActiveTab,
            toggleUserFollow,
            loadMore,
            gotoProfile,
            getFirstLetter
        }
    }
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.hover-lift {
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.hover-lift:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
}
</style>